import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ButtonWithIcon, CategoryIcon, CreateIcon, Grid, Spinner, actionIconStyles } from '../../../shared/components';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionScopes } from '../../auth/permissions';
import { ServiceAttributeDefinition, ServiceCategory, ServiceOffering } from '../types/Service';
import { getServiceCategories, getServices, serviceCatalogService } from '../services/serviceCatalogService';
import { formatAttributeSummary } from '../../catalog/CatalogAttributeFilters';
import { CatalogFilterPanel } from '../../catalog/CatalogFilterPanel';
import { formatMoney } from '../../invoices/invoiceForm';
import { useTenantCurrency } from '../../invoices/tenantCurrency';
import styles from '../../catalog/Catalog.module.css';

export const Services: React.FC = () => {
  const { t } = useTranslation(['services', 'common']);
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const currency = useTenantCurrency();
  const [items, setItems] = useState<ServiceOffering[]>([]);
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const [status, setStatus] = useState('');
  const [definitions, setDefinitions] = useState<ServiceAttributeDefinition[]>([]);
  const [attributeFilters, setAttributeFilters] = useState<Record<string, string>>({});
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const canCreate = hasPermission(PermissionScopes.servicesCreate);

  const activeAttributeFilters = useMemo(
    () => Object.fromEntries(Object.entries(attributeFilters).filter(([, value]) => value.trim())),
    [attributeFilters],
  );
  const attributeFilterKey = JSON.stringify(activeAttributeFilters);

  useEffect(() => {
    if (categoryIds.length !== 1) {
      setDefinitions([]);
      return;
    }

    const selectedCategoryId = categoryIds[0];
    let cancelled = false;
    void serviceCatalogService.getAttributes(selectedCategoryId, true)
      .then(items => {
        if (!cancelled) setDefinitions(items);
      })
      .catch(() => {
        if (!cancelled) setDefinitions([]);
      });

    return () => {
      cancelled = true;
    };
  }, [categoryIds]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [offerings, serviceCategories] = await Promise.all([
          getServices({
            page: 1,
            pageSize: 200,
            categoryIds: categoryIds.length > 0 ? categoryIds : undefined,
            status: status === 'active' || status === 'archived' ? status : undefined,
            attributes: Object.keys(activeAttributeFilters).length > 0 ? activeAttributeFilters : undefined,
          }),
          getServiceCategories(),
        ]);
        setItems(offerings);
        setCategories(serviceCategories);
        setError(null);
      } catch {
        setError(t('failedToLoad', { ns: 'services' }));
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [activeAttributeFilters, attributeFilterKey, categoryIds, status, t]);

  const filtered = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return items;
    return items.filter(item =>
      item.name.toLowerCase().includes(query)
      || (item.code || '').toLowerCase().includes(query)
      || (item.accountingCode || '').toLowerCase().includes(query)
      || item.categoryName.toLowerCase().includes(query)
      || Object.values(item.attributes).some(value => value.toLowerCase().includes(query)));
  }, [items, searchTerm]);

  const clearFilters = () => {
    setAttributeFilters({});
    setCategoryIds([]);
    setStatus('');
  };

  const resultLabel = filtered.length === 1
    ? t('resultCountOne', { ns: 'services' })
    : t('resultCount', { ns: 'services', count: filtered.length });

  return (
    <CatalogFilterPanel
      ns="services"
      title={t('title', { ns: 'services' })}
      headerActions={(
        <>
          <button
            className={`btn btn-secondary ${actionIconStyles.buttonWithIcon}`}
            type="button"
            onClick={() => navigate('/services/categories')}
          >
            <ButtonWithIcon icon={<CategoryIcon />}>{t('manageCategories', { ns: 'services' })}</ButtonWithIcon>
          </button>
          {canCreate ? (
            <button
              className={`btn btn-primary ${actionIconStyles.buttonWithIcon}`}
              type="button"
              onClick={() => navigate('/services/add')}
            >
              <ButtonWithIcon icon={<CreateIcon />}>{t('addService', { ns: 'services' })}</ButtonWithIcon>
            </button>
          ) : null}
        </>
      )}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder={t('searchPlaceholder', { ns: 'services' })}
      resultLabel={loading ? undefined : resultLabel}
      open={isFilterOpen}
      onOpenChange={setIsFilterOpen}
      categories={categories}
      categoryIds={categoryIds}
      onCategoryIdsChange={nextCategoryIds => {
        setAttributeFilters({});
        setCategoryIds(nextCategoryIds);
      }}
      status={status}
      onStatusChange={setStatus}
      definitions={definitions}
      attributeFilters={attributeFilters}
      onAttributeChange={(attributeId, value) => setAttributeFilters(prev => ({ ...prev, [attributeId]: value }))}
      onClearFilters={clearFilters}
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40 }}>
          <Spinner text={t('loading', { ns: 'services' })} />
        </div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : filtered.length === 0 ? (
        <div className={styles.emptyState}>{t('empty', { ns: 'services' })}</div>
      ) : (
        <Grid
          columns={[
            {
              key: 'name',
              label: t('name', { ns: 'services' }),
              render: (item: ServiceOffering) => (
                <div className={styles.nameWithThumb}>
                  {item.imageUrl
                    ? <img className={styles.listThumb} src={item.imageUrl} alt="" />
                    : <span className={`${styles.listThumb} ${styles.listThumbPlaceholder}`} />}
                  <div className={styles.valueStack}>
                    <span className={styles.primaryText}>{item.name}</span>
                    <span className={styles.secondaryText}>
                      {formatAttributeSummary(definitions, item.attributes, t('yes', { ns: 'services' }), t('no', { ns: 'services' }))
                        || item.code
                        || '-'}
                    </span>
                  </div>
                </div>
              ),
            },
            {
              key: 'category',
              label: t('category', { ns: 'services' }),
              render: (item: ServiceOffering) => <span className={styles.valueText}>{item.categoryName || '-'}</span>,
            },
            {
              key: 'salePrice',
              label: t('salePrice', { ns: 'services' }),
              render: (item: ServiceOffering) => <span className={styles.valueText}>{formatMoney(item.salePrice, currency)}</span>,
            },
            {
              key: 'durationMinutes',
              label: t('durationMinutes', { ns: 'services' }),
              render: (item: ServiceOffering) => <span className={styles.valueText}>{item.durationMinutes ?? '-'}</span>,
            },
            {
              key: 'status',
              label: t('status', { ns: 'services' }),
              render: (item: ServiceOffering) => (
                <span className={`${styles.statusBadge} ${item.status === 'archived' ? styles.statusArchived : styles.statusActive}`}>
                  {item.status === 'archived' ? t('statusArchived', { ns: 'services' }) : t('statusActive', { ns: 'services' })}
                </span>
              ),
            },
          ]}
          rows={filtered}
          rowKey={(item: ServiceOffering) => item.id}
          onRowClick={(item: ServiceOffering) => navigate(`/services/${item.id}`)}
        />
      )}
    </CatalogFilterPanel>
  );
};

export default Services;
